import { useRouter } from "expo-router";
import { useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { ScrollView } from "react-native";
import { loaderRef } from "../../src/components";
import { useAppDispatch, useAppSelector } from "../../src/hooks";
import { editUserData } from "../../src/store/user";

import {
  isWeb,
  useUpdateCustomerProfileMutation,
  useUpdateProfileImageMutation,
} from "@stepapp/core";
import { Block, Button, Divider, Input, ProfileInfo, Typography, palette } from "@stepapp/ui";
import { useTranslation } from "react-i18next";

type ProfileForm = {
  description: string;
  avatar: string;
};

const EditProfileScreen = () => {
  const profile = useAppSelector(({ user }) => user.profile);
  const { back } = useRouter();
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const [updateCustomerProfile] = useUpdateCustomerProfileMutation();
  const [updateProfileImage] = useUpdateProfileImageMutation();

  const defaultValues = useMemo(
    () => ({
      avatar: profile.avatar,
      description: profile.description,
    }),
    [profile.avatar, profile.description],
  );

  const {
    handleSubmit,
    setValue,
    watch,
    formState: { isDirty, dirtyFields },
  } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues,
    values: defaultValues,
  });

  const values = watch();

  const handleChangeDescription = useCallback(
    (value: string) => {
      setValue("description", value, { shouldDirty: true, shouldTouch: true });
    },
    [setValue],
  );

  const handlePhotoSelect = useCallback(
    (base64: string) => {
      setValue("avatar", `data:image/png;base64,${base64}`, { shouldDirty: true });
    },
    [setValue],
  );

  const onSubmit = handleSubmit(async ({ description, avatar }: ProfileForm) => {
    try {
      loaderRef.current?.showLoader();
      const isAvatarChanged = !!dirtyFields.avatar;
      const isDescriptionChanged = !!dirtyFields.description;

      if (isAvatarChanged) {
        await updateProfileImage({ variables: { data: { image: avatar } } });
      }

      if (isDescriptionChanged) {
        await updateCustomerProfile({
          variables: {
            data: {
              description,
            },
          },
        });
      }

      dispatch(editUserData({ profile: { ...profile, avatar, description } }));
      back();
    } catch (_) {
      alert(t("alerts.cantUpdate"));
    } finally {
      loaderRef.current?.hideLoader();
    }
  });

  return (
    <ScrollView>
      <Block paddingTop={isWeb ? 4 : 2}>
        <Typography variant="newH2">{t("screens.editProfile.title")}</Typography>

        <ProfileInfo
          avatar={values?.avatar}
          t={t}
          onPhotoSelect={handlePhotoSelect}
          marginTop={3}
        />

        <Block paddingBottom={3}>
          <Divider color={palette.secondary.grey} />
        </Block>

        <Typography variant="sectionTitle">{t("screens.editProfile.aboutMe")}</Typography>

        <Block marginTop={2.5}>
          <Input
            multiline
            onChangeText={handleChangeDescription}
            placeholder={t("components.input.placeholder.addNote")}
            value={values.description}
          />
        </Block>
      </Block>

      <Button
        disabled={!isDirty}
        label={t("screens.editProfile.updateInfo")}
        onPress={onSubmit}
        type="primary"
      />
    </ScrollView>
  );
};

export default EditProfileScreen;
